"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reminderSchema = exports.reminderRequestSchema = exports.signedInSchema = exports.signInVerifySchema = exports.signInRequestSchema = exports.guestDocumentListSchema = exports.guestDocumentSchema = exports.guestChecklistSchema = exports.guestChecklistItemSchema = exports.guestExchangeListSchema = exports.guestExchangeSummarySchema = exports.guestExchangeSchema = exports.verifiedSessionSchema = exports.genericLinkInfoSchema = exports.genericLinkIdentifyRequestSchema = exports.verifyRequestSchema = exports.challengeIssuedSchema = exports.openedGrantSchema = exports.CHALLENGE_CODE_PATTERN = void 0;
/**
 * The guest surface's wire types.
 *
 * Here rather than in the backend because the `guest` frontend renders every one
 * of these, and section 18 makes `shared/contracts` the only place a type crosses
 * that boundary. `core` is forbidden from importing this package precisely so
 * that a wire shape cannot quietly become a domain type: the domain has
 * `core/guest/service.ts`'s own results, and the controller maps.
 *
 * WHAT IS NOT HERE
 *
 * No grant token, no session token, no device cookie, no challenge code on any
 * response. The tokens travel in a path segment and in `Set-Cookie`; the code
 * travels by email. A field for any of them in a response body would be a
 * credential in a browser's network log and in every proxy in between.
 *
 * No internal identifiers either, beyond the exchange id a session is already
 * bound to and the ids of things *inside* that exchange which the surface has to
 * act on: a checklist item, because `POST /uploads/:id/commit` takes a
 * `requestId` and the prospect is the one choosing which item they are answering,
 * and a document, because a download needs to name one. Both are already scoped
 * to the session's single exchange by the row-level security policies, so a
 * captured response yields nothing that was not already reachable with the
 * cookie that fetched it. A grant id or a contact id still has no business here.
 */
const zod_1 = require("zod");
const api_1 = require("./api");
/** Section 08: six decimal digits. */
exports.CHALLENGE_CODE_PATTERN = /^\d{6}$/;
/**
 * What a visitor learns by presenting a live grant.
 *
 * Enough to recognise the request as genuine - who is asking, about what, and
 * where the code will go - because the real failure mode of a passwordless flow
 * is a recipient who cannot tell it from phishing.
 */
exports.openedGrantSchema = zod_1.z.object({
    exchangeTitle: zod_1.z.string(),
    repName: zod_1.z.string(),
    /** `j***@example.com`. Shown only to somebody holding a live grant. */
    maskedEmail: zod_1.z.string(),
    /** True when this browser can skip the emailed code. Section 08, step 2. */
    deviceTrusted: zod_1.z.boolean(),
    grantExpiresAt: zod_1.z.iso.datetime(),
    /**
     * True when this browser holds a live session belonging to a *different* contact.
     *
     * The surface cannot work this out for itself: the session is an `HttpOnly` cookie and no projection
     * carries a contact id, so "signed in" and "signed in as the person this link was sent to" look
     * identical from the browser. They are not the same on a device two people share, and a surface that
     * treated them as the same showed one person the other's documents from a link addressed to them.
     *
     * Defaulted, because it was added after the schema shipped and an older server answering without it
     * should leave the surface taking the cautious branch rather than failing to parse.
     */
    sessionIsAnotherContact: zod_1.z.boolean().default(false),
});
exports.challengeIssuedSchema = zod_1.z.object({
    expiresAt: zod_1.z.iso.datetime(),
    maskedEmail: zod_1.z.string(),
    /** So the surface can warn before the last attempt rather than after it. */
    attemptsRemaining: zod_1.z.number().int().min(0),
});
/**
 * The verification request.
 *
 * `code` is optional: absent means "use this browser's trusted device", which is
 * section 08's step 2. `rememberDevice` is the visitor's own opt-in and defaults
 * to false, because a default of true would be device trust nobody asked for.
 */
exports.verifyRequestSchema = zod_1.z.strictObject({
    code: zod_1.z.string().regex(exports.CHALLENGE_CODE_PATTERN, 'must be six digits').optional(),
    rememberDevice: zod_1.z.boolean().default(false),
});
/** Identity supplied before a reusable generic link enters the normal code flow. */
exports.genericLinkIdentifyRequestSchema = zod_1.z.strictObject({
    displayName: zod_1.z.string().trim().min(1).max(200),
    email: zod_1.z.string().trim().email().max(254),
});
exports.genericLinkInfoSchema = zod_1.z.object({
    valid: zod_1.z.boolean(),
    repName: zod_1.z.string().optional(),
});
/**
 * The response to a successful verification.
 *
 * The session itself is in a cookie. What comes back in the body is only what the
 * surface needs to render and to decide when to warn about expiry.
 */
exports.verifiedSessionSchema = zod_1.z.object({
    exchangeId: zod_1.z.uuid(),
    exchangeTitle: zod_1.z.string(),
    idleExpiresAt: zod_1.z.iso.datetime(),
    absoluteExpiresAt: zod_1.z.iso.datetime(),
    /** True when this browser was remembered, so the surface can say so. */
    deviceRemembered: zod_1.z.boolean(),
});
/** What the guest surface renders once a session exists. */
exports.guestExchangeSchema = zod_1.z.object({
    exchangeId: zod_1.z.uuid(),
    title: zod_1.z.string(),
    repName: zod_1.z.string(),
    contactName: zod_1.z.string(),
});
/**
 * Every exchange a signed-in contact is party to. D-25.
 *
 * The surface's first read, and what replaces the single `GET /guest/v1/exchange`. Each
 * entry carries `repName` because a contact who deals with two representatives has to be
 * able to tell one list from the other, and it is the only thing that distinguishes them:
 * two exchanges can carry similar titles and both are "documents you are exchanging".
 *
 * `openRequestCount` is here rather than left to a per-exchange fetch so the list can say
 * which one is waiting on the person reading it. That is the whole reason somebody signs
 * in, and making them open each exchange to find out would be the same failure DG.14 was.
 *
 * No document count beside it. It would need a second aggregate on every row to answer a
 * question the list does not raise, and the exchange itself answers it in one click.
 */
exports.guestExchangeSummarySchema = zod_1.z.object({
    exchangeId: zod_1.z.uuid(),
    title: zod_1.z.string(),
    repName: zod_1.z.string(),
    /** Awaiting or partially answered. Withdrawn and fulfilled items are not "open". */
    openRequestCount: zod_1.z.number().int().nonnegative(),
    /** Last change to the exchange or its checklist, so the surface can order and date it. */
    updatedAt: zod_1.z.iso.datetime(),
});
exports.guestExchangeListSchema = zod_1.z.object({
    /** The contact's own name, once, rather than repeated on every row. */
    contactName: zod_1.z.string(),
    exchanges: zod_1.z.array(exports.guestExchangeSummarySchema),
});
/**
 * One item on the checklist, as the prospect sees it.
 *
 * Figure 5's "Michael needs from you". The state is what decides whether the row offers an
 * upload button or reads "Sent", and `withdrawn` items are sent rather than filtered
 * server-side: the surface hides them, and a client that wants to show "no longer needed"
 * can. Filtering here would mean the API could not express what the rep actually did.
 *
 * The guest surface says "Sent" where `/api/v1` says "Received"; figure 5 drew the rep's
 * word on both. It is one state named from whichever end is reading it, which is why the
 * wire word is `fulfilled` and neither surface's vocabulary reaches this file.
 *
 * `fulfilledBy` names the document that answered it, which is one the prospect sent
 * themselves. It is what lets the surface link that pill to the file behind it.
 */
exports.guestChecklistItemSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    /** The rep's own words, shown verbatim. Reps ask for three specific things. */
    label: zod_1.z.string(),
    state: api_1.requestStateSchema,
    fulfilledBy: zod_1.z.uuid().nullable(),
    requestedAt: zod_1.z.iso.datetime(),
});
exports.guestChecklistSchema = zod_1.z.object({
    requests: zod_1.z.array(exports.guestChecklistItemSchema),
});
/**
 * One document in the exchange, either direction.
 *
 * Narrower than `/api/v1`'s `documentSchema` on purpose, the same way
 * `guestExchangeSchema` is narrower than `exchangeSchema`. A prospect has no use for a
 * version integer - there is no `If-Match` on this surface - and `currentVersionId` would
 * be an identifier they can do nothing with.
 *
 * No `removedAt`, because a soft-deleted document never reaches this list: the guest read
 * goes through `Documents.list`, which filters them out. R4 keeps the row for a records
 * production, not for the person on the other end of it.
 */
exports.guestDocumentSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    name: zod_1.z.string(),
    /** Which side sent it. Figure 5 splits the list on exactly this. */
    origin: zod_1.z.enum(['rep', 'contact']),
    addedAt: zod_1.z.iso.datetime(),
});
exports.guestDocumentListSchema = zod_1.z.object({
    documents: zod_1.z.array(exports.guestDocumentSchema),
});
/**
 * Asking for a sign-in code. D-25.
 *
 * An address and nothing else. `strictObject` so a client sending anything more is told,
 * and no `rememberDevice` here - that is a choice made with the code, not before it.
 *
 * The response has no schema because there is no body: the endpoint answers 202 with
 * nothing, whether or not the address is one we hold. See `signInRequested` below for why
 * the surface has to be written against that.
 */
exports.signInRequestSchema = zod_1.z.strictObject({
    // Length-bounded before anything looks at it: this is the one unauthenticated endpoint
    // that takes free text, and an address is a lookup key rather than a document.
    email: zod_1.z.string().trim().toLowerCase().min(3).max(320).pipe(zod_1.z.email()),
    /** Optional generic-link context; when present, sign-in is scoped to that link's rep. */
    genericLinkToken: zod_1.z.string().min(1).max(256).optional(),
});
/**
 * What the surface must show after asking, and it is deliberately not an answer.
 *
 * There is no schema for a response because the endpoint returns none. Recorded here as a
 * comment rather than a type so the reason travels with the contract: telling a caller
 * whether an address is known would publish the firm's client list to anybody willing to
 * iterate, and for a financial firm that list is the sensitive thing. Every request is
 * answered 202 with an empty body, and the surface says "if we know that address, a code
 * is on its way" without claiming one was sent.
 */
/** Submitting the code. The address again, because nothing was kept between the two calls. */
exports.signInVerifySchema = zod_1.z.strictObject({
    email: zod_1.z.string().trim().toLowerCase().min(3).max(320).pipe(zod_1.z.email()),
    code: zod_1.z.string().regex(exports.CHALLENGE_CODE_PATTERN, 'A confirmation code is six digits.'),
    rememberDevice: zod_1.z.boolean().default(false),
    /** Optional generic-link context; when present, sign-in is scoped to that link's rep. */
    genericLinkToken: zod_1.z.string().min(1).max(256).optional(),
});
/**
 * What a successful sign-in tells the surface.
 *
 * No exchange in it, unlike `verifiedSessionSchema`. A sign-in is not about one piece of
 * work, so the surface goes on to `GET /guest/v1/exchanges` to find out what there is.
 */
exports.signedInSchema = zod_1.z.object({
    idleExpiresAt: zod_1.z.iso.datetime(),
    absoluteExpiresAt: zod_1.z.iso.datetime(),
    deviceRemembered: zod_1.z.boolean(),
});
/**
 * Nudging a contact who was already told. Rep-facing, so it is on `/api/v1`.
 *
 * Replaces `invitationRequestSchema`, and the difference is the whole point of the change:
 * a rep no longer asks us to introduce somebody, because requesting or sending a document
 * does that by itself. What is left is the case the automatic message does not cover, which
 * is a person who was told and has not acted. So this reminds rather than invites, and it
 * mints nothing the ordinary path would not have minted anyway.
 *
 * An empty body. It is a `strictObject` rather than nothing so that a client sending a field
 * we have not defined is told rather than having it ignored, which is the same argument
 * section 15 makes about configuration keys.
 */
exports.reminderRequestSchema = zod_1.z.strictObject({});
exports.reminderSchema = zod_1.z.object({
    /**
     * Which message went, because the button's meaning depends on the exchange's state and the
     * rep should see which reading we took. `requested` when something is still outstanding on
     * the checklist, `shared` when nothing is and there are documents waiting to be read.
     *
     * Deliberately no `delivered` flag, unlike the invitation this replaces. That endpoint sent
     * inline and could report what the provider said; this one enqueues on the outbox, so the
     * honest answer at response time is "accepted for delivery" and nothing more.
     */
    sent: zod_1.z.enum(['requested', 'shared']),
});
//# sourceMappingURL=guest.js.map